import { MDBCardBody, MDBCardHeader } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faHandshake, faLock, faMapMarker, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useRecoilState } from "recoil";
import {
    ConfirmationModal,
    Input,
    NewImageUpload,
    RichEditor,
    RowInfo,
} from "../common";
import { Button, Row, Col, NpcItemActionButtonWrapper, Divider } from "../App.styles";
import { highlightIdState, useNpcItemsQuery, useQueryInvalidateInfinite, useQueryNoInvalidate } from "./Npc.state";
import { useGameQuery } from "../settings/Settings.state";
import usePreviewButton from "../hooks/usePreviewButton";
import DescriptionModal from "./modals/DescriptionModal";
import TitleModal from "./modals/TitleModal";
import ImageModal from "./modals/ImageModal";
import { OrganizeContextProvider } from '../organize/Organize.context'
import AdvancedSettings from '../common/AdvancedSettings'
import { useDecision } from '@optimizely/react-sdk'
import { useMemo } from 'react'
import LocksSection from './sections/LocksSection'
import LocationSection from './sections/LocationSection'
import TimersSection from './sections/TimersSection'
import InteractionsSection from './sections/InteractionsSection'

const NpcItemEdit = ({ id, onClose, onDelete: destroy, isDeleting }) => {
    const { t } = useTranslation()
    const useQuery = (config) => useNpcItemsQuery(id, config)
    const useQueryNoInvalidate_ = () => useQueryNoInvalidate(id)
    const useQueryInvalidateInfinite_ = () => useQueryInvalidateInfinite(id)
    const [{ code }] = useGameQuery();
    const [, setHighlightId] = useRecoilState(highlightIdState)
    const onSave = async () => {
        await onClose()
        setHighlightId(id)
    }
    const previewBtnProps = usePreviewButton({ code, path: 'messaging' })
    const [decisionNpcLocks] = useDecision('npc-locks');
    const [decisionNpcLocation] = useDecision('npc-location');
    const [decisionNpcTimers] = useDecision('npc-timers');
    const [decisionNpcInteractions] = useDecision('npc-interactions');
    const shouldShowAdvancedSettings = useMemo(() => {
        return decisionNpcLocks?.enabled ||
            decisionNpcLocation?.enabled ||
            decisionNpcTimers?.enabled ||
            decisionNpcInteractions?.enabled
    }, [
        decisionNpcLocks,
        decisionNpcLocation,
        decisionNpcTimers,
        decisionNpcInteractions,
    ])

    const ActionDelete = () => (
        <ConfirmationModal
            title={t("Confirm Deletion")}
            body={t("Are you sure you wish to delete?")}
            confirmLabel={t("Delete")}
            confirmAction={() => {
                onClose && onClose()
                destroy(id)
            }}
            isLoading={isDeleting}
        >
            <Button disabled={isDeleting} className="btn--flat" color="warning">
                <FontAwesomeIcon icon={faTrash} /> {t('Delete')}
            </Button>
        </ConfirmationModal>
    )

    const settingsTabs = useMemo(() => {
        const tabs = []
        if (decisionNpcLocks?.enabled) {
            tabs.push({
                title: <><FontAwesomeIcon icon={faLock} /> {t('Locks')}</>, 
                content: <LocksSection useQuery={useQueryNoInvalidate_} />,
            })
        }
        if (decisionNpcLocation?.enabled) {
            tabs.push({
                title: <><FontAwesomeIcon icon={faMapMarker} /> {t('Location')}</>, 
                content: <LocationSection useQuery={useQueryNoInvalidate_} />,
            })
        }
        if (decisionNpcTimers?.enabled) {
            tabs.push({
                title: <><FontAwesomeIcon icon={faClock} /> {t('Timers')}</>, 
                content: <TimersSection useQuery={useQueryNoInvalidate_} />,
            })
        }
        if (decisionNpcInteractions?.enabled) {
            tabs.push({
                title: <><FontAwesomeIcon icon={faHandshake} /> {t('Interactions')}</>, 
                content: <InteractionsSection useQuery={useQueryNoInvalidate_} />,
            })
        }
        return tabs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        decisionNpcLocks?.enabled,
        decisionNpcLocation?.enabled,
        decisionNpcTimers?.enabled,
        t,
    ])

    return (
        <>
            <MDBCardHeader>
                <Row>
                    <Col size="12" lg="auto" className="align-items-start mb-4 col--stretch col--grow">
                        <RowInfo help={<TitleModal />}>
                            <Input
                                data-testid="npc-item__input-title"
                                name="name"
                                label={t("NPC Name")}
                                useQuery={useQueryInvalidateInfinite_}
                                mode="clean"
                            />
                        </RowInfo>
                    </Col>
                </Row>
            </MDBCardHeader>
            <MDBCardBody>
                <Row>
                    <Col size="12" lg="auto" className="align-items-start mb-4 col--stretch col--grow">
                        <RowInfo help={<ImageModal />}>
                            <NewImageUpload name="media" label={t("Image")} useQuery={useQuery} standalone />
                        </RowInfo>
                    </Col>
                </Row>
                <div className="mt-4 mt-lg-0 mb-4">
                    <RowInfo help={<DescriptionModal />}>
                        <RichEditor
                            help={t("This is the main text for your challenge.")}
                            label={t("Description")}
                            name="prompt"
                            useQuery={useQueryNoInvalidate_}
                        />
                    </RowInfo>
                </div>
                {
                    shouldShowAdvancedSettings
                        ? (
                            <OrganizeContextProvider>
                                <AdvancedSettings
                                    tabs={settingsTabs}
                                />
                            </OrganizeContextProvider>
                        )
                        : null
                }
                <Divider />
                <NpcItemActionButtonWrapper className="justify-content-between d-flex mb-md-2 g-2">
                    <Col size="6" lg="4" className="col--stretch">
                        <ActionDelete />
                    </Col>
                    <Col size="6" lg="4" className="col--stretch justify-content-center">
                        <Button
                            className="btn--flat"
                            rel="noreferrer"
                            outline
                            {...previewBtnProps}
                        >
                            {t('Preview')}
                        </Button>
                    </Col>
                    <Col size="6" lg="4" className="col--stretch justify-content-end">
                        <Button data-testid="npc-item__button-save" color="primary" className="btn--flat" onClick={onSave}>
                            {t('Save')}
                        </Button>
                    </Col>
                </NpcItemActionButtonWrapper>
            </MDBCardBody>
        </>
    );
};

export default NpcItemEdit;
